import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { getAllStories, getAllStoryTags } from '../../api';
import { Notifications } from '../../store/slices';
import {
    DEFAULT_PER_PAGE,
    HIDDEN_STORY_FILTERS,
    IStoryViewerFilters,
    IStoryViewerStory,
    ITagCategory,
    ITagItem,
    PER_PAGE_OPTIONS,
} from '../../lib/storyViewer';

export type LoadStatus = 'idle' | 'loading' | 'resolved' | 'rejected';

export const parseFilters = (params: URLSearchParams): IStoryViewerFilters => {
    const perPage = Number(params.get('perPage'));
    return {
        occasionId: params.get('occasionId') || '',
        recipientId: params.get('recipientId') || '',
        productId: params.get('productId') || '',
        tags: (params.get('tags') || '')
            .split(',')
            .map((v) => Number(v))
            .filter((n) => Number.isFinite(n) && n > 0),
        page: Math.max(1, Number(params.get('page')) || 1),
        perPage: PER_PAGE_OPTIONS.includes(perPage) ? perPage : DEFAULT_PER_PAGE,
    };
};

export const serializeFilters = (filters: IStoryViewerFilters): URLSearchParams => {
    const params = new URLSearchParams();
    if (filters.occasionId) params.set('occasionId', String(filters.occasionId));
    if (filters.recipientId) params.set('recipientId', String(filters.recipientId));
    if (filters.productId) params.set('productId', String(filters.productId));
    if (filters.tags.length) params.set('tags', filters.tags.join(','));
    if (filters.page > 1) params.set('page', String(filters.page));
    if (filters.perPage !== DEFAULT_PER_PAGE) params.set('perPage', String(filters.perPage));
    return params;
};

export const hasActiveFilters = (filters: IStoryViewerFilters) =>
    Boolean(filters.occasionId || filters.recipientId || filters.productId || filters.tags.length);

export const useStoryViewerData = () => {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const filters = useMemo(() => parseFilters(searchParams), [searchParams]);
    const tagsKey = filters.tags.join(',');

    const [stories, setStories] = useState<IStoryViewerStory[]>([]);
    const [total, setTotal] = useState(0);
    const [status, setStatus] = useState<LoadStatus>('idle');
    const [tags, setTags] = useState<ITagItem[]>([]);
    const [categories, setCategories] = useState<ITagCategory[]>([]);
    const requestId = useRef(0);

    useEffect(() => {
        let cancelled = false;
        getAllStoryTags({ page: 1, perPage: 200 })
            .then((res) => {
                if (cancelled) return;
                setTags(((res?.result || []) as ITagItem[]).filter((t) => !t.isDeleted));
                setCategories(
                    ([...(res?.category || [])] as ITagCategory[]).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
                );
            })
            .catch((error: any) => {
                if (cancelled) return;
                dispatch(Notifications.setNotifications({ text: error?.message || 'Failed to load tags', type: 'error' }));
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const id = ++requestId.current;
        setStatus('loading');
        getAllStories({
            ...HIDDEN_STORY_FILTERS,
            page: filters.page,
            perPage: filters.perPage,
            occasionId: filters.occasionId,
            recipientId: filters.recipientId,
            productId: filters.productId,
            tags: tagsKey,
        })
            .then((res) => {
                if (id !== requestId.current) return;
                setStories((res?.result || []) as IStoryViewerStory[]);
                setTotal(Number(res?.totalCount) || 0);
                setStatus('resolved');
            })
            .catch((error: any) => {
                if (id !== requestId.current) return;
                setStatus('rejected');
                dispatch(Notifications.setNotifications({ text: error?.message || 'Failed to load stories', type: 'error' }));
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.page, filters.perPage, filters.occasionId, filters.recipientId, filters.productId, tagsKey]);

    const updateFilters = useCallback(
        (patch: Partial<IStoryViewerFilters>, options: { keepPage?: boolean } = {}) => {
            const next: IStoryViewerFilters = { ...filters, ...patch };
            if (!options.keepPage) next.page = 1;
            setSearchParams(serializeFilters(next));
        },
        [filters, setSearchParams],
    );

    const clearFilters = useCallback(() => {
        setSearchParams(serializeFilters({ ...filters, occasionId: '', recipientId: '', productId: '', tags: [], page: 1 }));
    }, [filters, setSearchParams]);

    return { filters, stories, total, status, tags, categories, updateFilters, clearFilters };
};
